import React, { useEffect, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getDefaultOption } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { getAllComponents } from "utils/enclosure/components";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import ceilingCodeTemplate from "features/Model/Enclosure/Templates/ceilingCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import MultiSelect from "components/Input/MultiSelect";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";

import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import EmbodiedCarbonFramingGroupGrade, { EmbodiedCarbonCalculationsSummary, StandardEmbodiedLayerBlock } from "components/EmbodiedCarbonGrade";
import globalStyles from "components/globalStyles.module.scss";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const interiorValidation = getValidation("csCeilingInterior");
const structureValidation = getValidation("csCeilingStructureType");
const typeSizeValidation = getValidation("csCeilingComponentTypeSize");
const flatTypeSizeValidation = getValidation("csCeilingFlatComponentTypeSize");
const framingSpacingValidation = getValidation("csCeilingFramingSpacing");
const ins1Validation = getValidation("csCeilingInsLayer1");
const flatIns1Validation = getValidation("csCeilingFlatInsLayer1");
const ins2Validation = getValidation("csCeilingInsLayer2");

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const StandardCeilingCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isFlat = false,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { ceilingInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } = useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } = useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const ceilingCode =
            useStateSelector(`${drawerAccessor}.ceilingCode`) || {};
        const { id: ceilingStructureId, value: ceilingStructureValue = 0 } =
            useStateSelector(`${drawerAccessor}.ceilingCode.layers.structureType`) || {};
        const { id: ceilingSpacingId, value: ceilingSpacingValue } =
            useStateSelector(`${drawerAccessor}.ceilingCode.layers.spacing`) || {};
        const { value: ceilingTypeSizeValue } =
            useStateSelector(`${drawerAccessor}.ceilingCode.layers.componentTypeSize`) || {};
        const { value: ceilingFramingValue } = useStateSelector(`${drawerAccessor}.ceilingCode.layers.framing`) || {};
        const { value: ceilingIns1Value } =
            useStateSelector(`${drawerAccessor}.ceilingCode.layers.insulationLayer1`) || {};
        const { value: ceilingIns2Value } =
            useStateSelector(`${drawerAccessor}.ceilingCode.layers.insulationLayer2`) || {};
        const { value: ceilingInteriorValue } = useStateSelector(`${drawerAccessor}.ceilingCode.layers.interior`) || {};
        const ceilingLayers = useStateSelector(`${drawerAccessor}.ceilingCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.ceilingCode.layers`;
        const ceilingLabel = useStateSelector(`${drawerAccessor}.ceilingCode.label`) || "";

        const frameSpacingValue = ceilingSpacingId === null ? ceilingFramingValue : ceilingSpacingValue;
        const ceilingValue = `2${ceilingStructureValue
            .toString()
            .substr(
                -1
            )}${ceilingTypeSizeValue}${frameSpacingValue}${ceilingIns1Value}${ceilingIns2Value}${ceilingInteriorValue}${0}${0}${0}`;

        const isSolid = parseFloat(ceilingStructureId) === 4;
        const isPanel = parseFloat(ceilingStructureId) === 5;

        const makeSpacingDefault = ["5", "6", "7", "8", "9", "A", "B", "C"].includes(ceilingTypeSizeValue);
        const makeInsulationDefault = ["6", "7", "8", "9", "A", "B", "C"].includes(ceilingTypeSizeValue);

        const { codeRef: editingCodeRef = null, isLibCode = false } = ceilingInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath(isFlat ? "CeilingFlat" : "Ceiling", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];
        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"]) ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents) : selectedComponents;

        const valueRef = useRef(null);

        const handleStuctureChange = useCallback(
            ({ id, value }) => {
                if (isFlat) {
                    change(
                        `${drawerAccessor}.ceilingCode.layers.insulationLayer1`,
                        getDefaultOption("csCeilingFlatInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.ceilingCode.layers.componentTypeSize`,
                        getFirstOption({ indKey: id, depKey: "csCeilingFlatComponentTypeSize" })
                    );
                } else {
                    change(
                        `${drawerAccessor}.ceilingCode.layers.insulationLayer1`,
                        getDefaultOption("csCeilingInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.ceilingCode.layers.componentTypeSize`,
                        getFirstOption({ indKey: id, depKey: "csCeilingComponentTypeSize" })
                    );
                }

                // Use "Framing" when set to Solid, use Spacing otherwise
                if (parseFloat(id) === 4) {
                    //set spacing to null and grab first option for framing
                    change(`${drawerAccessor}.ceilingCode.layers.spacing`, { id: null });
                    change(
                        `${drawerAccessor}.ceilingCode.layers.framing`,
                        getFirstOption({
                            indKey: id,
                            depKey: "csCeilingFramingSpacing",
                        })
                    );
                } else {
                    //set framing to null and grab first option for spacing
                    change(`${drawerAccessor}.ceilingCode.layers.framing`, { id: null });

                    // Only grab default spacing id if not set
                    if (ceilingSpacingId === null) {
                        change(
                            `${drawerAccessor}.ceilingCode.layers.spacing`,
                            getDefaultOption("csCeilingFramingSpacing")
                        );
                    }
                }

                // Panel
                if (parseFloat(id) === 5) {
                    change(
                        `${drawerAccessor}.ceilingCode.layers.insulationLayer1`,
                        getDefaultOption(isFlat ? "csCeilingFlatInsLayer1" : "csCeilingInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.ceilingCode.layers.insulationLayer2`,
                        getDefaultOption("csCeilingInsLayer2")
                    );
                }
            },
            [ceilingStructureId, change, ceilingSpacingId]
        );

        useEffect(() => {
            const initialValues = {
                ceilingCode: isEmpty(ceilingInsType) ? ceilingCodeTemplate : ceilingInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = ceilingValue;
        }, [ceilingValue, ceilingLabel]);

        let availableComponents = getAllComponents(isFlat ? "ceiling-flat" : "ceiling", components, fieldAccessor).sort(
            codeSort
        );

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const ceilingEmbComponentType = isFlat ? "ceilingFlat" : "ceiling";
        const ceilingEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>{`Standard ${isFlat ? "Flat Ceiling" : "Ceiling"} Code`}</h3> */}
                <InputRow gridTemplate={CodeTypeSelect || (!isCodeLibrary && !isUpgradeComponent) ? "1fr 1fr 1fr" : "1fr 1fr"}  style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.ceilingCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={ceilingLabel.toString() === valueRef.current ? ceilingValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.ceilingCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={ceilingValue}
                    />
                    {!isCodeLibrary && !isUpgradeComponent && (
                        <Field
                            component={MultiSelect}
                            name={`${drawerAccessor}.selectedComponents`}
                            label={`Apply to multiple components`}
                            options={availableComponents.map(({ disabled = false, label = "", ...rest }) => ({
                                ...rest,
                                disabled,
                                label: disabled ? `${label} (current component)` : label,
                            }))}
                            hasSelectAll
                        />
                    )}
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <Field
                                    component={Checkbox}
                                    name={`${drawerAccessor}.addToLibrary`}
                                    label={isLibCode ? "Update code in library" : "Add to code library"}
                                    large
                                    disabled={isLibCode}
                                    type="checkbox"
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                            />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.ceilingCode.layers.structureType`}
                            label="Structure Type"
                            placeholder="Choose Structure Type"
                            options={getOptions({ fieldKey: "csCeilingStructureType" })}
                            validate={structureValidation}
                            onChange={handleStuctureChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.ceilingCode.layers.componentTypeSize`}
                            label="Component Type / Size"
                            placeholder="Choose Type / Size"
                            options={getOptions({
                                fieldKey: isFlat ? "csCeilingFlatComponentTypeSize" : "csCeilingComponentTypeSize",
                                indKey: ceilingStructureId,
                            })}
                            validate={isFlat ? flatTypeSizeValidation : typeSizeValidation}
                            onChange={({ value = "" }) => {
                                if (["6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(
                                        `${drawerAccessor}.ceilingCode.layers.insulationLayer1`,
                                        getDefaultOption(isFlat ? "csCeilingFlatInsLayer1" : "csCeilingInsLayer1")
                                    );
                                    change(
                                        `${drawerAccessor}.ceilingCode.layers.insulationLayer2`,
                                        getDefaultOption("csCeilingInsLayer2")
                                    );
                                }
                                if (["5", "6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(`${drawerAccessor}.ceilingCode.layers.framing`, { id: 0, value: "0" });
                                }
                            }}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        {isSolid ? (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.ceilingCode.layers.framing`}
                                label="Framing"
                                placeholder="Choose Framing"
                                options={getOptions({
                                    fieldKey: "csCeilingFramingSpacing",
                                    indKey: ceilingStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={makeSpacingDefault}
                                isFlatDesign={true}
                                customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                            />
                        ) : (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.ceilingCode.layers.spacing`}
                                label="Spacing"
                                placeholder="Choose Spacing"
                                options={getOptions({
                                    fieldKey: "csCeilingFramingSpacing",
                                    indKey: ceilingStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={isPanel}
                                isFlatDesign={true}
                                customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                            />
                        )}
                    </InputRow>
                    <EmbodiedCarbonFramingGroupGrade
                        layers={ceilingLayers}
                        componentType={ceilingEmbComponentType}
                        customPadding="4px 0 0 0"
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        customGap={"1rem"}
                        barMetric={barMetric}
                    />
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer1"
                    layers={ceilingLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType={ceilingEmbComponentType}
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={ceilingEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ceilingCode.layers.insulationLayer1`}
                        label="Framing Cavity Insulation (Layer 1)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: isFlat ? "csCeilingFlatInsLayer1" : "csCeilingInsLayer1" })}
                        validate={isFlat ? flatIns1Validation : ins1Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer2"
                    layers={ceilingLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType={ceilingEmbComponentType}
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={ceilingEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ceilingCode.layers.insulationLayer2`}
                        label="Continuous Layer of Insulation (Layer 2)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csCeilingInsLayer2" })}
                        validate={ins2Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="interior"
                    layers={ceilingLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType={ceilingEmbComponentType}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...ceilingEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ceilingCode.layers.interior`}
                        label="Interior"
                        placeholder="Choose Interior"
                        options={getOptions({ fieldKey: "csCeilingInterior" })}
                        validate={interiorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={ceilingCode}
                    componentType={ceilingEmbComponentType}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.ceilingCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isFlat,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "ceilingInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isFlat,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            ceilingCode = {},
            addToLibrary = false,
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        const ceilCodeType = isFlat ? "CeilingFlat" : "Ceiling";

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        toggleOpen(false);

        // Run standard code logic
        standardCodeLogic({
            change,
            componentCodeType: ceilCodeType,
            componentId,
            code: ceilingCode,
            inputName: "ceilingInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardCeilingCode);
